import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

export interface ContentPageProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  subtitle?: string;
  content: React.ReactNode;
  sidebar?: React.ReactNode;
  showToc?: boolean;
  sections?: { id: string; title: string }[];
}

export default function ContentPage({ title, subtitle, content, showToc, sections }: ContentPageProps) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<string>('');

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="container-main">
          <div className="grid lg:grid-cols-[1fr_280px] gap-10">
            {/* Main Content */}
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
                <a href="/" className="hover:text-primary-500 transition-colors">{t('nav.home')}</a>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-700">{title}</span>
              </nav>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-3">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">{subtitle}</p>
              )}

              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-6">
                {content}
              </div>

              {/* Internal Links */}
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">{t('content.relatedPages')}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <a href="/" className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors">
                    <i className="ri-home-line text-primary-500"></i>
                    <span className="text-sm text-foreground-700">{t('nav.home')}</span>
                  </a>
                  <a href="/cssbuy-spreadsheet/" className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors">
                    <i className="ri-table-2 text-primary-500"></i>
                    <span className="text-sm text-foreground-700">{t('nav.spreadsheet')}</span>
                  </a>
                  <a href="/cssbuy-review/" className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors">
                    <i className="ri-file-list-3-line text-primary-500"></i>
                    <span className="text-sm text-foreground-700">{t('nav.review')}</span>
                  </a>
                  <a href="/cssbuy-shipping-cost/" className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors">
                    <i className="ri-truck-line text-primary-500"></i>
                    <span className="text-sm text-foreground-700">{t('nav.shipping')}</span>
                  </a>
                </div>
              </div>

              {/* Quick Category Links */}
              <div className="mt-8">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">{t('content.browseCategories')}</h3>
                <div className="flex flex-wrap gap-2">
                  {categories.slice(0, 8).map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-3 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-800 rounded-full hover:bg-secondary-200 transition-colors"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-24 space-y-6">
                {showToc && sections && (
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-3">{t('content.tableOfContents')}</h3>
                    <ul className="space-y-1.5">
                      {sections.map((s) => (
                        <li key={s.id}>
                          <button
                            onClick={() => scrollTo(s.id)}
                            className={`text-xs text-left w-full hover:text-primary-500 transition-colors ${activeSection === s.id ? 'text-primary-500 font-medium' : 'text-foreground-600'}`}
                          >
                            {s.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA Card */}
                <div className="bg-primary-500 rounded-xl p-5 text-white">
                  <h3 className="text-sm font-semibold mb-2">{t('content.continueShopping')}</h3>
                  <p className="text-xs text-white/80 mb-4">{t('home.cta.description')}</p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 text-white text-xs font-medium rounded-full hover:bg-white/25 transition-colors"
                  >
                    {t('content.visitMain')}
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}